/**
 * Live collaboration types — Q&A, announcements, polls (CodeLive).
 */

export type CollabParticipant = {
  identity: string;
  role: string;
  display_name: string;
};

export type QaAnswer = {
  body: string;
  answered_by: CollabParticipant;
  answered_at?: string | null;
};

export type QaItem = {
  qa_id: string;
  session_id: string;
  asked_by: CollabParticipant;
  question: string;
  status: "open" | "answered" | "dismissed";
  is_anonymous?: boolean;
  topic_id?: string | null;
  topic_title?: string;
  answer?: QaAnswer | null;
  pinned?: boolean;
  visible_to_all?: boolean;
  created_at?: string | null;
};

export type LiveAnnouncement = {
  announcement_id: string;
  session_id: string;
  title: string;
  body: string;
  author?: CollabParticipant | null;
  pinned: boolean;
  status: string;
  created_at?: string | null;
  published_at?: string | null;
};

export type PollOption = {
  id: string;
  text: string;
  votes: number;
};

export type PollVote = {
  student_id: string;
  student_name: string;
  option_id: string;
  voted_at?: string | null;
};

export type LivePoll = {
  poll_id: string;
  session_id: string;
  question: string;
  options: PollOption[];
  status: "active" | "ended";
  total_votes: number;
  missed_count?: number;
  ends_at: number;
  created_by?: CollabParticipant | null;
  my_vote_id?: string | null;
  my_missed?: boolean;
  created_at?: string | null;
  /** Trainer: voters per option */
  votes?: PollVote[];
};

export const LIVE_QA_TYPE = "codelive.qa" as const;
export const LIVE_ANNOUNCEMENT_TYPE = "codelive.announcement" as const;
export const LIVE_POLL_TYPE = "codelive.poll" as const;

export type LiveQaMessage = {
  type: typeof LIVE_QA_TYPE;
  v: 1;
  item: QaItem;
};

export type LiveAnnouncementMessage = {
  type: typeof LIVE_ANNOUNCEMENT_TYPE;
  v: 1;
  item: LiveAnnouncement;
};

export type LivePollMessage = {
  type: typeof LIVE_POLL_TYPE;
  v: 1;
  poll: LivePoll;
};

export const LIVE_CHAT_TYPE = "codelive.chat" as const;
export const LIVE_CHAT_ACCESS_TYPE = "codelive.chat-access" as const;
/** Compact like/reaction delta — avoids rebroadcasting full chat bodies. */
export const LIVE_CHAT_ENGAGE_TYPE = "codelive.chat-engage" as const;
/** Drop an optimistic bubble if Mongo persist failed. */
export const LIVE_CHAT_RETRACT_TYPE = "codelive.chat-retract" as const;
export const LIVE_QA_RETRACT_TYPE = "codelive.qa-retract" as const;
export const LIVE_ANNOUNCEMENT_RETRACT_TYPE =
  "codelive.announcement-retract" as const;

export type ChatAccess = {
  session_id: string;
  students_chat_enabled: boolean;
  students_group_chat_enabled: boolean;
  students_can_message_trainer: boolean;
};

export type ChatReaction = {
  emoji: string;
  count: number;
  i_reacted: boolean;
};

export type ChatMessage = {
  message_id: string;
  session_id: string;
  channel: "public" | "dm";
  thread_id?: string | null;
  sender: CollabParticipant;
  recipient?: CollabParticipant | null;
  body: string;
  created_at?: string | null;
  like_count?: number;
  i_liked?: boolean;
  reactions?: ChatReaction[];
};

/** Quick emoji strip under chat bubbles (matches room reaction set). */
export const CHAT_QUICK_EMOJIS = [
  "👍",
  "👏",
  "❤️",
  "😂",
  "😮",
  "🎉",
  "🔥",
  "🙌",
] as const;

export type ChatThreadPeer = {
  thread_id: string;
  peer: CollabParticipant;
};

export type LiveChatMessage = {
  type: typeof LIVE_CHAT_TYPE;
  v: 1;
  message: ChatMessage;
};

export type LiveChatAccessMessage = {
  type: typeof LIVE_CHAT_ACCESS_TYPE;
  v: 1;
  access: ChatAccess;
};

export type LiveChatEngageMessage = {
  type: typeof LIVE_CHAT_ENGAGE_TYPE;
  v: 1;
  message_id: string;
  like_count: number;
  reactions: ChatReaction[];
  at: number;
};

export function makeChatThreadId(a: string, b: string): string {
  return [String(a).trim(), String(b).trim()]
    .sort((x, y) => x.toLowerCase().localeCompare(y.toLowerCase()))
    .join(":");
}

/** Client-side id for optimistic chat / Q&A / announcements / polls. */
export function newCollabId(prefix: "msg" | "qa" | "ann" | "poll"): string {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${prefix}-${hex}`;
}
